import { type RefObject, useCallback, useEffect, useRef } from 'react';
import { type BlockerFunction, useBlocker } from 'react-router';

const UNLOAD_EVENT = 'beforeunload' as const;

interface UseUnsavedChangesGuardParams {
  isDirty: boolean;
  isSaving: boolean;
  save: () => Promise<boolean>;
  discard: () => void | Promise<void>;
  // A ref (not state) so a caller can flip it and navigate in the same tick —
  // the blocker reads .current at call time, so it sees the flip even though
  // the render that would reflect a state update hasn't happened yet.
  bypassGuardRef?: RefObject<boolean>;
}

interface UseUnsavedChangesGuardResult {
  isBlocked: boolean;
  onSave: () => Promise<void>;
  onDiscard: () => Promise<void>;
  onCancel: () => void;
}

export function useUnsavedChangesGuard({
  isDirty,
  isSaving,
  save,
  discard,
  bypassGuardRef,
}: UseUnsavedChangesGuardParams): UseUnsavedChangesGuardResult {
  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ currentLocation, nextLocation }) =>
        isDirty &&
        !isSaving &&
        !bypassGuardRef?.current &&
        currentLocation.pathname !== nextLocation.pathname,
      [isDirty, isSaving, bypassGuardRef],
    ),
  );

  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;

  useEffect(() => {
    if (!isDirty) {
      return undefined;
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener(UNLOAD_EVENT, handleBeforeUnload);
    return () => {
      window.removeEventListener(UNLOAD_EVENT, handleBeforeUnload);
    };
  }, [isDirty]);

  const onSave = useCallback(async () => {
    const ok = await save();
    if (ok) {
      blockerRef.current.proceed?.();
    } else {
      blockerRef.current.reset?.();
    }
  }, [save]);

  const onDiscard = useCallback(async () => {
    await discard();
    blockerRef.current.proceed?.();
  }, [discard]);

  const onCancel = useCallback(() => {
    blockerRef.current.reset?.();
  }, []);

  return {
    isBlocked: blocker.state === 'blocked',
    onSave,
    onDiscard,
    onCancel,
  };
}
